import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { NotificationProvider } from "./context/NotificationContext";
import { FavoritesProvider } from "./context/FavoritesContext";
import { Toaster } from "./components/ui/toaster";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import ProductList from "./pages/ProductList";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import ThankYou from "./pages/ThankYou";
import GuestOrder from "./pages/GuestOrder";
import AdminReviews from "./pages/admin/AdminReviews";
import MyPurchases from "./pages/MyPurchases";
import Login from "./pages/Login";
import Profile from "./pages/Profile";
import SellerProducts from "./pages/SellerProducts";
import SellerQuestions from "./pages/SellerQuestions";
import SellerOrders from "./pages/SellerOrders";
import StoreConfig from "./pages/StoreConfig";
import StorePage from "./pages/StorePage";
import AdvertiserCampaigns from "./pages/AdvertiserCampaigns";
import EvolutionaryDashboard from "./pages/EvolutionaryDashboard";
import UserDashboardLayout from "./components/UserDashboardLayout";
import PersonalInfo from "./pages/account/PersonalInfo";
import AccountDetails from "./pages/account/AccountDetails";
import Security from "./pages/account/Security";
import Addresses from "./pages/account/Addresses";
import Privacy from "./pages/account/Privacy";
import UserWallet from "./pages/account/UserWallet";
import UserSubscription from "./pages/account/UserSubscription";
import UserFavorites from "./pages/account/UserFavorites";
import TerminosPage from "./pages/legal/Terminos";
import PrivacidadPage from "./pages/legal/Privacidad";
import AcercaPage from "./pages/legal/Acerca";
import DeslindePage from "./pages/legal/Deslinde";
import UserOrderDetail from "./pages/UserOrderDetail";
import SellerLanding from "./pages/SellerLanding";
import Register from "./pages/Register";
import ExpressRegisterPopup from "./components/ExpressRegisterPopup";
import AppDownloadBanner from "./components/AppDownloadBanner";
import { RouteTitle } from "./components/RouteTitle";
import AdminStores from "./pages/admin/AdminStores";

// Admin pages
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminDeliveryDrivers from "./pages/admin/AdminDeliveryDrivers";
import AdminAds from "./pages/admin/AdminAds";
import AdminWarRoomPage from "./pages/admin/AdminWarRoom";
import AdminAppConfig from "./pages/admin/AdminAppConfig";
import AdminPreferences from "./pages/admin/AdminPreferences";
import {
  AdminRequests,
  AdminBlog,
  AdminTaxes,
  AdminCMS,
  AdminImportExport,
  AdminShipping,
  AdminSettings,
} from "./pages/admin/AdminPlaceholders";
import { AdminMarketing } from "./pages/admin/AdminMarketingReal";
import AdminSEO from "./pages/admin/AdminSEO";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminAlgorithms from "./pages/admin/AdminAlgorithms";
import AdminSearchEngine from "./pages/admin/AdminSearchEngine";
import AdminIntelligence from "./pages/admin/AdminIntelligence";
import AdminOrderDetail from "./pages/admin/AdminOrderDetail";
import AdminBank from "./pages/admin/AdminBank";
import AdsLanding from "./pages/AdsLanding";

// Delivery pages
import DeliveryLogin from "./pages/delivery/DeliveryLogin";
import DeliveryRegister from "./pages/delivery/DeliveryRegister";
import DeliveryDashboard from "./pages/delivery/DeliveryDashboard";
import DeliveryOrders from "./pages/delivery/DeliveryOrders";
import {
  DeliveryAvailable,
  DeliveryHistory,
  DeliveryProfile,
} from "./pages/delivery/DeliveryPlaceholders";

// Admin route wrapper
const AdminRoute = ({ children }) => {
  const token = localStorage.getItem('admin_token');
  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
};

// Delivery route wrapper
const DeliveryRoute = ({ children }) => {
  const token = localStorage.getItem('delivery_token');
  if (!token) {
    return <Navigate to="/delivery/login" replace />;
  }
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <RouteTitle />
      <AuthProvider>
        <CartProvider>
          <NotificationProvider>
            <FavoritesProvider>
            <Routes>
              {/* ==================== ADMIN ROUTES ==================== */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="/admin/warroom" element={<AdminRoute><AdminWarRoomPage /></AdminRoute>} />
              <Route path="/admin/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
              <Route path="/admin/reviews" element={<AdminRoute><AdminReviews /></AdminRoute>} />
              <Route path="/admin/requests" element={<AdminRoute><AdminRequests /></AdminRoute>} />
              <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
              <Route path="/admin/orders/:orderId" element={<AdminRoute><AdminOrderDetail /></AdminRoute>} />
              <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
              <Route path="/admin/delivery-drivers" element={<AdminRoute><AdminDeliveryDrivers /></AdminRoute>} />
              <Route path="/admin/ads" element={<AdminRoute><AdminAds /></AdminRoute>} />
              <Route path="/admin/marketing" element={<AdminRoute><AdminMarketing /></AdminRoute>} />
              <Route path="/admin/blog" element={<AdminRoute><AdminBlog /></AdminRoute>} />
              <Route path="/admin/taxes" element={<AdminRoute><AdminTaxes /></AdminRoute>} />
              <Route path="/admin/cms" element={<AdminRoute><AdminCMS /></AdminRoute>} />
              <Route path="/admin/intelligence" element={<AdminRoute><AdminIntelligence /></AdminRoute>} />
              <Route path="/admin/import-export" element={<AdminRoute><AdminImportExport /></AdminRoute>} />
              <Route path="/admin/shipping" element={<AdminRoute><AdminShipping /></AdminRoute>} />
              <Route path="/admin/seo" element={<AdminRoute><AdminSEO /></AdminRoute>} />
              <Route path="/admin/payments" element={<AdminRoute><AdminPayments /></AdminRoute>} />
              <Route path="/admin/algorithms" element={<AdminRoute><AdminAlgorithms /></AdminRoute>} />
              <Route path="/admin/search-engine" element={<AdminRoute><AdminSearchEngine /></AdminRoute>} />
              <Route path="/admin/bank" element={<AdminRoute><AdminBank /></AdminRoute>} />
              <Route path="/admin/stores" element={<AdminRoute><AdminStores /></AdminRoute>} />
              <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
              <Route path="/admin/app-config" element={<AdminRoute><AdminAppConfig /></AdminRoute>} />
              <Route path="/admin/preferences" element={<AdminRoute><AdminPreferences /></AdminRoute>} />

              {/* ==================== DELIVERY ROUTES ==================== */}
              {/* Isolated UI for delivery drivers - no navbar, no cart, no catalog */}
              <Route path="/delivery/login" element={<DeliveryLogin />} />
              <Route path="/delivery/register" element={<DeliveryRegister />} />
              <Route path="/delivery" element={<DeliveryRoute><DeliveryDashboard /></DeliveryRoute>} />
              <Route path="/delivery/orders" element={<DeliveryRoute><DeliveryOrders /></DeliveryRoute>} />
              <Route path="/delivery/available" element={<DeliveryRoute><DeliveryAvailable /></DeliveryRoute>} />
              <Route path="/delivery/history" element={<DeliveryRoute><DeliveryHistory /></DeliveryRoute>} />
              <Route path="/delivery/profile" element={<DeliveryRoute><DeliveryProfile /></DeliveryRoute>} />

              {/* ==================== MAIN STORE ROUTES ==================== */}
              {/* For standard users: buyers, sellers, advertisers (unified experience) */}
              <Route
                path="/*"
                element={
                  <div className="App min-h-screen flex flex-col">
                    <AppDownloadBanner />
                    <Navbar />
                    <ExpressRegisterPopup />
                    <main className="flex-1">
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/search" element={<ProductList />} />
                        <Route path="/category/:category" element={<ProductList />} />
                        <Route path="/product/:id" element={<ProductDetail />} />
                        <Route path="/cart" element={<Cart />} />
                        <Route path="/checkout" element={<Checkout />} />
                        <Route path="/guest-order/:orderId" element={<GuestOrder />} />
                        <Route path="/thank-you" element={<ThankYou />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/vender" element={<SellerLanding />} />
                        {/* === Private user routes with sidebar layout === */}
                        <Route element={<UserDashboardLayout />}>
                          <Route path="/profile" element={<Navigate to="/account/wallet" replace />} />
                          <Route path="/orders" element={<Navigate to="/account/wallet" replace />} />
                          <Route path="/account/personal" element={<PersonalInfo />} />
                          <Route path="/account/details" element={<AccountDetails />} />
                          <Route path="/account/security" element={<Security />} />
                          <Route path="/account/addresses" element={<Addresses />} />
                          <Route path="/account/privacy" element={<Privacy />} />
                          <Route path="/account/wallet" element={<UserWallet />} />
                          <Route path="/account/subscription" element={<UserSubscription />} />
                          <Route path="/my-purchases" element={<MyPurchases />} />
                          <Route path="/order/:orderId" element={<UserOrderDetail viewAs="buyer" />} />
                          <Route path="/buyer/favorites" element={<UserFavorites />} />
                          <Route path="/seller" element={<EvolutionaryDashboard />} />
                          <Route path="/seller/products" element={<SellerProducts />} />
                          <Route path="/seller/orders" element={<SellerOrders />} />
                          <Route path="/seller/orders/:orderId" element={<UserOrderDetail viewAs="seller" />} />
                          <Route path="/seller/questions" element={<SellerQuestions />} />
                          <Route path="/store-config" element={<StoreConfig />} />
                          <Route path="/advertiser/campaigns" element={<AdvertiserCampaigns />} />
                        </Route>
                        <Route path="/store/:sellerId" element={<StorePage />} />
                        {/* Legal pages */}
                        <Route path="/terminos" element={<TerminosPage />} />
                        <Route path="/privacidad" element={<PrivacidadPage />} />
                        <Route path="/acerca" element={<AcercaPage />} />
                        <Route path="/deslinde" element={<DeslindePage />} />
                        <Route path="/ads" element={<AdsLanding />} />
                      </Routes>
                    </main>
                    <Footer />
                  </div>
                }
              />
            </Routes>
            <Toaster />
            </FavoritesProvider>
          </NotificationProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
